import { formatMoney } from '@hunk/shared/common';
import { productIdFromParam } from '@hunk/shared/productUrl';
import { ChevronLeft, ChevronRight, Loader2, Minus, Plus, ShieldCheck, ShoppingBag } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';

import { Price, ProductGrid, ProductImage } from '@/components/ProductCard';
import { useAppNavigate } from '@/hooks/useAppNavigate';
import { useCart } from '@/hooks/useCart';
import { useShop } from '@/hooks/useShop';
import { cn } from '@/lib/utils';
import { DeliveryIcon } from '@/pages/CheckoutPage';
import { NotFound } from '@/pages/HomePage';

export function ProductPage() {
  const { id: param } = useParams();
  const id = productIdFromParam(param);
  const { byId, products, categories, delivery, loading } = useShop();
  const { add, qtyOf } = useCart();
  const navigate = useAppNavigate();
  const product = byId.get(id);
  const [img, setImg] = useState(0);
  const [qty, setQty] = useState(1);
  useEffect(() => {
    setImg(0);
    setQty(1);
    window.scrollTo(0, 0);
  }, [id]);

  const related = useMemo(
    () => (product ? products.filter((p) => p.id !== product.id && p.inStock && p.categoryIds.some((c) => product.categoryIds.includes(c))).slice(0, 8) : []),
    [product, products]
  );

  if (loading)
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="text-primary size-10 animate-spin" />
      </div>
    );
  if (!product) return <NotFound />;

  const images = product.images;
  const cats = categories.filter((c) => product.categoryIds.includes(c.id));
  const inCart = qtyOf(product.id);
  const room = Math.max(0, product.maxQty - inCart);

  return (
    <main className="mx-auto max-w-[1280px] px-3 py-5 md:px-4 md:py-8">
      <nav className="mb-4 flex flex-wrap items-center gap-1 text-sm text-neutral-400">
        <Link to="/" className="hover:text-white">
          ראשי
        </Link>
        {cats[0] && (
          <>
            <ChevronLeft className="size-4" />
            <Link to={`/category/${encodeURIComponent(cats[0].slug)}`} className="hover:text-white">
              {cats[0].name}
            </Link>
          </>
        )}
      </nav>

      <div className="grid gap-6 md:grid-cols-2 md:gap-10">
        {/* gallery */}
        <div>
          <div className="relative aspect-square overflow-hidden rounded-[22px] bg-white p-4">
            <ProductImage src={images[img]} alt={product.name} />
            {images.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={() => setImg((i) => (i + 1) % images.length)}
                  className="absolute top-1/2 left-2 -translate-y-1/2 rounded-full bg-black/60 p-2 text-white"
                  aria-label="התמונה הבאה"
                >
                  <ChevronLeft className="size-5" />
                </button>
                <button
                  type="button"
                  onClick={() => setImg((i) => (i - 1 + images.length) % images.length)}
                  className="absolute top-1/2 right-2 -translate-y-1/2 rounded-full bg-black/60 p-2 text-white"
                  aria-label="התמונה הקודמת"
                >
                  <ChevronRight className="size-5" />
                </button>
              </>
            )}
          </div>
          {images.length > 1 && (
            <div className="scrollbar-none mt-3 flex gap-2 overflow-x-auto">
              {images.map((src, i) => (
                <button
                  key={src}
                  type="button"
                  onClick={() => setImg(i)}
                  className={cn('size-16 shrink-0 overflow-hidden rounded-lg border-2 bg-white p-1 md:size-20', i === img ? 'border-primary' : 'border-transparent')}
                >
                  <img src={src} alt="" className="h-full w-full object-contain" loading="lazy" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* details */}
        <div className="flex flex-col gap-4">
          <h1 className="text-2xl leading-tight font-extrabold md:text-3xl">{product.name}</h1>
          <Price product={product} big />
          {product.sku && <p className="text-xs text-neutral-500">מק"ט: {product.sku}</p>}

          {product.inStock ? (
            <>
              {product.lowStock != null && <p className="text-sm font-semibold text-amber-400">נותרו {product.lowStock} במלאי</p>}
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center rounded-full border border-white/20">
                  <button type="button" onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-2.5" aria-label="פחות">
                    <Minus className="size-4" />
                  </button>
                  <span className="w-8 text-center font-bold">{qty}</span>
                  <button type="button" onClick={() => setQty((q) => Math.min(room || 1, q + 1))} className="p-2.5" aria-label="עוד">
                    <Plus className="size-4" />
                  </button>
                </div>
                <button
                  type="button"
                  disabled={room === 0}
                  onClick={() => add(product.id, Math.min(qty, room))}
                  className="brand-grad brand-grad-hover flex flex-1 items-center justify-center gap-2 rounded-full px-6 py-3 text-base font-bold text-white shadow disabled:opacity-50 sm:flex-none"
                >
                  <ShoppingBag className="size-5" />
                  הוספה לסל
                </button>
              </div>
              {inCart > 0 && (
                <button type="button" onClick={() => navigate('/cart')} className="self-start text-sm font-semibold text-emerald-400 hover:underline">
                  {inCart} בסל · מעבר לסל הקניות ←
                </button>
              )}
            </>
          ) : (
            <p className="self-start rounded-full bg-neutral-800 px-5 py-2.5 font-bold text-neutral-400">אזל מהמלאי</p>
          )}

          <ul className="mt-2 flex flex-col gap-2 rounded-2xl border border-white/10 p-4 text-sm text-neutral-300">
            {delivery?.options.map((o) => (
              <li key={o.id} className="flex items-center gap-2">
                <DeliveryIcon kind={o.kind} className="text-primary size-5 shrink-0" />
                <span>
                  {o.name} {o.fee ? formatMoney(o.fee) : 'ללא עלות'}
                  {o.fee > 0 && o.freeFrom != null ? ` · חינם מעל ${formatMoney(o.freeFrom)}` : ''}
                </span>
              </li>
            ))}
            <li className="flex items-center gap-2">
              <ShieldCheck className="text-primary size-5 shrink-0" />
              אפשרות לאריזה דיסקרטית - בלי שם החנות ובלי פירוט המוצרים
            </li>
          </ul>

          {product.description && <div className="mt-2 text-sm leading-relaxed whitespace-pre-line text-neutral-300 md:text-base">{product.description}</div>}
        </div>
      </div>

      {related.length > 0 && (
        <section className="mt-12">
          <h2 className="mb-4 text-xl font-extrabold md:text-2xl">מוצרים דומים</h2>
          <ProductGrid products={related} />
        </section>
      )}
    </main>
  );
}
